import {
    FormControl,
    Text,
    TextInput,
    DatePicker,
    IconButton,
    useShortcut,
} from '~/molecules';
import { temporalFromDate, formatTemporal, dateInputSpec, parseTemporal } from '@/shared/utils/temporal';
import { useTemporalInput, type TemporalDraftProps } from '../useTemporalInput';
import { MaskedInput } from '~/molecules/components/TextInput/MaskedInput';
import { ReadonlyField } from '../ReadonlyField';
import { StyleSheet } from 'react-native-unistyles';
import { useId, useRef, useState, type ReactNode } from 'react';
import { View } from 'react-native';
import getConfig from '~/config';
import { FocusTrap } from 'focus-trap-react';
import { DateFieldCalendar } from './DateFieldCalendar';
import { localeMap, parseValue } from './utils';

export interface DateFieldProps extends TemporalDraftProps {
    temporalType?: 'date' | 'datetime';
    disabled?: boolean;
    isReadonly?: boolean;
    name?: string;
    nativeID?: string;
    accessibilityLabel?: string;
    onChange?: (value: string | null) => void;
    placeholder?: string;
    required?: boolean;
    title?: ReactNode;
    tooltip?: string;
    value?: string | null;
    validationMessage?: ReactNode;
}

const FOCUS_TRAP_OPTIONS = {
    allowOutsideClick: true,
    initialFocus: false,
} as const;

export const DateField = (props: DateFieldProps) => {
    const {
        temporalType = 'date',
        isReadonly,
        name,
        accessibilityLabel,
        onChange,
        title,
        tooltip,
        required,
        validationMessage,
        value,
        temporalDraft,
        onTemporalDraftChange,
        ...rest
    } = props;

    const config = getConfig();
    const configLocale = config.translations?.locale || 'de';
    const locale = localeMap[configLocale.split('-')[0]] || 'de';

    const [open, setOpen] = useState(false);
    const fieldRef = useRef<View>(null);

    useShortcut('modal-close', () => {
        if (open) setOpen(false);
    });

    const includesTime = temporalType === 'datetime';
    const tokens: Record<string, string> = { day: 'dd', month: 'MM', year: 'yyyy' };
    const dateFormat = dateInputSpec(configLocale).map(part => tokens[part.type] ?? part.value).join('') + (includesTime ? ' HH:mm' : '');
    const input = useTemporalInput(temporalType, value, onChange, { temporalDraft, onTemporalDraftChange }, configLocale);
    const errorId = useId();

    if (isReadonly) {
        return <ReadonlyField name={name} label={title} value={formatTemporal(value, includesTime ? 'datetime' : 'date', configLocale)} tooltip={tooltip} />;
    }

    const dateValue = parseValue(value);
    const handleChange = (date: Date | null) => {
        if (props.disabled) return;
        // The picker chooses a calendar day. Preserve the stored wall clock as
        // text: converting it through Date can shift times across a DST gap.
        const clock = parseTemporal(value, 'datetime')?.slice(11) ?? '00:00:00';
        const next = date ? temporalFromDate(date, 'date') + (includesTime ? `T${clock}` : '') : null;
        input.commit(next);
        setOpen(false);
    };

    return (
        <FormControl disabled={rest.disabled}>
            <FormControl.Label required={required} label={title} tooltip={tooltip} />
            <DatePicker.Provider
                disabled={rest.disabled}
                locale={locale}
                open={open}
                onChange={handleChange}
                onOpenChange={(nextOpen) => setOpen(nextOpen && !props.disabled)}
                value={dateValue}
            >
                <View ref={fieldRef}>
                    <MaskedInput
                        {...rest}
                        {...input.inputProps}
                        mask={dateFormat}
                        keyboardType="number-pad"
                        accessibilityLabel={accessibilityLabel ?? name ?? (typeof title === 'string' ? title : undefined)}
                        aria-required={!!required}
                        aria-describedby={validationMessage ? errorId : undefined}
                        aria-invalid={!!validationMessage}
                        error={!!(validationMessage)}
                        placeholderTextColor={props.disabled ? styles.disabledText.color : styles.placeholderTextColor.color}
                        style={styles.datePickerInput}
                    >
                        <TextInput.Right>
                            <IconButton
                                accessibilityLabel="calendar"
                                disabled={props.disabled}
                                color={props.disabled ? styles.disabledIcon.color : styles.iconButton.color}
                                name="calendar"
                                onPress={() => {
                                    if (!props.disabled) setOpen((prev) => !prev);
                                }}
                                shape="square"
                                size={18}
                                style={styles.iconButton}
                            />
                        </TextInput.Right>
                    </MaskedInput>
                </View>
                <DatePicker.Popover
                    align="start"
                    offset={4}
                    position="bottom"
                    style={styles.datePickerPopover}
                    triggerRef={fieldRef}
                >
                    <FocusTrap active={open} focusTrapOptions={FOCUS_TRAP_OPTIONS}>
                        <View collapsable={false}>
                            <DateFieldCalendar containerStyle={styles.calendarContainer} />
                        </View>
                    </FocusTrap>
                </DatePicker.Popover>
            </DatePicker.Provider>
            {validationMessage && <Text nativeID={errorId} role="alert" style={styles.validationError}>{validationMessage}</Text>}
        </FormControl>
    );
};

DateField.displayName = 'DateField';

const styles = StyleSheet.create((theme) => ({
    validationError: { color: theme.colors.error, fontSize: 13 },
    disabledText: { color: theme.colors.onSurfaceDisabled },
    disabledIcon: { color: theme.colors.onSurface },
    calendarContainer: {
        backgroundColor: theme.colors.calendarBackground,
        minHeight: 0,
        minWidth: 300,
        width: 300,
    },
    datePickerInput: {
        minWidth: 100,
    },
    datePickerPopover: {
        backgroundColor: theme.colors.calendarBackground,
        borderRadius: theme.shapes.corner.small,
        width: 300,
    },
    iconButton: {
        color: theme.colors.primary,
        height: 30,
        width: 30,
    },
    placeholderTextColor: {
        color: theme.colors.onSurfaceVariant,
    },
}));
